<template>
  <v-app>
    <!-- Top Logo/Login Bar -->
    <v-app-bar
      v-if="!isLoginPage || isLoggedIn"
      app
      height="60"
      color="white"
      elevation="1"
    >
      <!-- Hamburger Menu -->
      <v-app-bar-nav-icon v-if="isLoggedIn" @click="toggleDrawer" class="app-bar-btn-primary"></v-app-bar-nav-icon>

      <!-- Logo aligned to the left -->
      <router-link to="/" class="d-flex align-center text-decoration-none ml-2">
        <img
          :src="logoUrl"
          alt="Banjino's Western Spritz"
          style="height: 40px; max-width: 180px; object-fit: contain; cursor: pointer;"
        />
      </router-link>

      <v-spacer></v-spacer>

      <!-- Interface Mode Switcher -->
      <v-btn-toggle
        v-if="isLoggedIn && !isLoginPage && !compactScreen && !isSalesWorkspace"
        v-model="interfaceMode"
        mandatory
        density="compact"
        class="mr-4"
        variant="outlined"
        divided
      >
        <v-btn value="simple" size="small">
          <v-icon start size="small">mdi-speedometer</v-icon>
          Simple
        </v-btn>
        <v-btn value="advanced" size="small">
          <v-icon start size="small">mdi-tune</v-icon>
          Advanced
        </v-btn>
      </v-btn-toggle>

      <!-- Unit System Toggle -->
      <UnitToggle
        v-if="isLoggedIn && !isLoginPage && !compactScreen && !isSalesWorkspace"
        class="mr-2"
      />

      <!-- Refresh Button -->
      <v-btn v-if="!isLoginPage" icon="mdi-refresh" @click="refreshData" variant="text" class="mr-2 app-bar-btn-primary"></v-btn>

      <!-- Login/User Section -->
      <div v-if="!isLoggedIn && !isLoginPage" class="d-flex align-center mr-3">
        <v-btn variant="text" class="app-bar-btn-primary" @click="handleLogin">
          <v-icon start>mdi-login</v-icon>
          Login
        </v-btn>
      </div>

      <!-- User Menu (when logged in) -->
      <v-menu v-else-if="isLoggedIn">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            color="primary"
            class="mr-3 app-bar-btn-primary"
          >
            <v-icon start>mdi-account-circle</v-icon>
            <span v-if="!compactScreen">{{ currentUser }}</span>
            <v-icon end>mdi-menu-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item prepend-icon="mdi-tune" title="Display options" @click="displayOptions = true" />
          <v-list-item
            prepend-icon="mdi-account"
            title="Profile"
            @click="handleProfile"
          ></v-list-item>
          <v-list-item
            prepend-icon="mdi-cog"
            title="Settings"
            @click="handleSettings"
          ></v-list-item>
          <v-divider></v-divider>
          <v-list-item
            prepend-icon="mdi-logout"
            title="Logout"
            @click="handleLogout"
          ></v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- Side Navigation Drawer -->
    <v-navigation-drawer
      v-if="isLoggedIn"
      v-model="drawer"
      app
      :rail="!compactScreen && railState"
      :expand-on-hover="!compactScreen"
      class="navigation-drawer-offset"
      :permanent="!compactScreen"
      :temporary="compactScreen"
      @update:rail="railState = $event"
    >

      <v-list density="compact" nav style="padding-top: 10px; padding-bottom: 0;">
        <template v-for="item in menuItems" :key="item.title">
          <v-tooltip
            location="end"
            :open-delay="200"
            :disabled="!railState"
          >
            <template v-slot:activator="{ props }">
              <v-list-item
                v-bind="props"
                :to="item.to"
                :prepend-icon="item.icon"
                :title="item.title"
                color="primary"
                :value="item.to"
                :class="[railState ? 'nav-item-collapsed' : 'nav-item-expanded', 'nav-item-with-tooltip']"
              ></v-list-item>
            </template>
            <span>{{ item.title }}</span>
          </v-tooltip>
        </template>
      </v-list>
    </v-navigation-drawer>

    <v-dialog v-model="displayOptions" max-width="620">
      <v-card title="Display options">
        <v-card-text>
          <v-btn-toggle v-model="interfaceMode" mandatory class="mb-4"><v-btn value="simple">Simple</v-btn><v-btn value="advanced">Advanced</v-btn></v-btn-toggle>
          <UnitToggle class="flex-wrap" />
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn @click="displayOptions = false">Done</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-main>
      <v-container fluid>
        <CrmWorkspaceNav v-if="isSalesWorkspace" />
        <v-alert v-if="isSalesWorkspace && route.query.calendarKind && route.query.calendarId" variant="tonal" type="info" class="mb-4">
          <CalendarInviteButton :key="`${route.query.calendarKind}-${route.query.calendarId}`" :kind="route.query.calendarKind" :id="route.query.calendarId" auto label="Add it to Google or Outlook instead" />
          <span class="text-body-2">The calendar file downloaded. If importing it is awkward — it is, on Gmail — add the events straight to Google or Outlook above. Saved calendar copies do not update automatically.</span>
        </v-alert>
        <router-view />
      </v-container>
    </v-main>

    <!-- App Footer -->
    <AppFooter />

    <!-- Global Toast Notification -->
    <v-snackbar
      v-model="toastState.show"
      :color="toastState.color"
      :timeout="toastState.timeout"
      top
      right
      rounded="pill"
      elevation="6"
    >
      {{ toastState.message }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="toastState.show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
  </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import axios from '@/services/axios'
import logoUrl from './assets/images/banjinos-logo-black.png'
import { usePermissionsStore } from './stores/permissions'
import { useAuthStore } from './stores/auth'
import { useUnitsStore } from './stores/units'
import { useToast } from '@/composables/useToast'
import { isLockedRole } from '@/utils/permissions'
import AppFooter from './components/AppFooter.vue'
import UnitToggle from './components/common/UnitToggle.vue'
import CalendarInviteButton from './components/crm/CalendarInviteButton.vue'
import CrmWorkspaceNav from './components/crm/CrmWorkspaceNav.vue'

const { smAndDown: compactScreen } = useDisplay()
const drawer = ref(!compactScreen.value)
const displayOptions = ref(false)
const railState = ref(false) // Start expanded so users can see full menu
// Load interface mode from localStorage with 'advanced' as default
const savedMode = localStorage.getItem('interfaceMode')
const interfaceMode = ref(savedMode || 'advanced')

// Save rail state preference
watch(railState, (newVal) => {
  localStorage.setItem('navigationRailState', newVal ? 'collapsed' : 'expanded')
})

// Persist interface mode to localStorage
watch(interfaceMode, (newMode) => {
  localStorage.setItem('interfaceMode', newMode)
})

// Load saved rail state preference
onMounted(() => {
  const savedRailState = localStorage.getItem('navigationRailState')
  if (savedRailState === 'collapsed' || (!savedRailState && isSalesWorkspace.value)) {
    railState.value = true
  }
})

const route = useRoute()
const router = useRouter()
const isSalesWorkspace = computed(() => route.path.startsWith('/crm') || route.path === '/sales-orders')

// Initialize stores directly - Pinia is available at this point
const permissionsStore = usePermissionsStore()
const authStore = useAuthStore()
const unitsStore = useUnitsStore()
const { toastState } = useToast()

// Toggle drawer method
const toggleDrawer = () => {
  if (compactScreen.value) { drawer.value = !drawer.value; return }
  if (!drawer.value) {
    // If drawer is closed, open it
    drawer.value = true
    railState.value = false // Expand when opening
  } else if (railState.value) {
    // If drawer is open but collapsed, expand it
    railState.value = false
  } else {
    // If drawer is open and expanded, collapse it
    railState.value = true
  }
}
watch(compactScreen, (compact) => { drawer.value = !compact })
watch(() => route.path, () => { if (compactScreen.value) drawer.value = false })

const menuItems = computed(() => {
  const items = []

  if (!authStore.isAuthenticated) {
    return [{ title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/' }]
  }

  if (isSalesWorkspace.value) return [
    { title: 'Today', icon: 'mdi-checkbox-marked-circle-outline', to: '/crm/today' },
    { title: 'Accounts', icon: 'mdi-store-outline', to: '/crm/accounts' },
    { title: 'Orders & deliveries', icon: 'mdi-truck-delivery-outline', to: '/sales-orders' },
    { title: 'All tools', icon: 'mdi-apps', to: '/' }
  ]

  // Determine user's primary focus based on permissions
  const hasContentPermissions = permissionsStore?.hasPermission('content.view') ||
                               permissionsStore?.hasPermission('content.manage')
  const hasManufacturingPermissions = permissionsStore?.hasPermission('materials.view') ||
                                     permissionsStore?.hasPermission('recipes.view') ||
                                     permissionsStore?.hasPermission('batches.view') ||
                                     permissionsStore?.hasPermission('products.view')
  const hasAdminPermissions = permissionsStore?.hasPermission('users.manage_permissions') ||
                             permissionsStore?.hasPermission('system.admin')

  // Show appropriate dashboard based on user's permissions
  if (hasContentPermissions && !hasManufacturingPermissions) {
    items.push({ title: 'Content Dashboard', icon: 'mdi-view-dashboard-variant', to: '/content-dashboard' })
  } else {
    items.push({ title: 'Dashboard', icon: 'mdi-view-dashboard', to: '/' })
  }

  // Simple Mode - Streamlined workflow
  if (interfaceMode.value === 'simple') {
    const isOwner = isLockedRole(authStore.user?.role);

    // Content Studio
    if (isOwner || permissionsStore?.hasPermission('content.manage')) {
      items.push({
        title: 'Content Studio',
        icon: 'mdi-movie-edit',
        to: '/content-studio'
      })
    }

    // Business operations
    if (isOwner || permissionsStore?.hasPermission('inventory.view') || permissionsStore?.hasPermission('recipes.view')) {
      items.push({
        title: 'Business',
        icon: 'mdi-store',
        to: '/business'
      })
    }

    // Analytics (simplified)
    if (isOwner || permissionsStore?.hasPermission('analytics.view')) {
      items.push({
        title: 'Analytics',
        icon: 'mdi-chart-box',
        to: '/advanced-optimization'
      })
    }

    // Events revenue engine
    if (isOwner || permissionsStore?.hasPermission('events.view')) {
      items.push({ title: 'Events', icon: 'mdi-calendar-star', to: '/events/today' })
    }

    // Retail CRM
    if (isOwner || permissionsStore?.hasPermission('crm.view')) {
      items.push({ title: 'Sales CRM', icon: 'mdi-storefront-outline', to: '/crm/today' })
    }

    // Fan art — what the public made, waiting on a human before it joins the mosaic.
    if (isOwner || permissionsStore?.hasPermission('fanart.view')) {
      items.push({ title: 'Fan art', icon: 'mdi-palette', to: '/fan-art' })
    }

    // Trade universe — the licensed world the CRM's doors are chosen from.
    if (isOwner || permissionsStore?.hasPermission('trade.view')) {
      items.push(
        { title: 'Trade map', icon: 'mdi-map-marker-radius', to: '/trade/map' },
        { title: 'Placements', icon: 'mdi-package-variant-closed-check', to: '/trade/placements' }
      )
    }

    // Compliance
    if (isOwner || permissionsStore?.hasPermission('compliance.view')) {
      items.push({ title: 'Compliance', icon: 'mdi-shield-check', to: '/compliance' })
    }

    // API Keys (owner / super-admin)
    if (isOwner || permissionsStore?.hasPermission('api_keys.manage')) {
      items.push({ title: 'API Keys', icon: 'mdi-key-variant', to: '/api-keys' })
    }

    // Settings
    items.push({ title: 'Settings', icon: 'mdi-cog', to: '/settings' })

    return items
  }

  // Advanced Mode - Full flat menu (no sub-menus)
  if (authStore?.isAuthenticated) {
    // Owner is locked full-access. Admin sees items via the permission map
    // (role defaults + per-user overlays).
    const isOwner = isLockedRole(authStore.user?.role);

    // Materials & Inventory
    if (isOwner || permissionsStore?.hasPermission('inventory.view')) {
      items.push(
        { title: 'Materials', icon: 'mdi-food-variant', to: '/materials' },
        { title: 'Material Inventory', icon: 'mdi-package-variant', to: '/inventory' },
        { title: 'Product Inventory', icon: 'mdi-bottle-soda', to: '/product-inventory' },
        { title: 'Purchase Orders', icon: 'mdi-clipboard-list', to: '/purchase-orders' }
      )
    }

    // Recipes & Products
    if (isOwner || permissionsStore?.hasPermission('recipes.view')) {
      items.push(
        { title: 'Recipes', icon: 'mdi-book-open-variant', to: '/recipes' },
        { title: 'Products', icon: 'mdi-package-variant-closed', to: '/products' },
        { title: 'Production Batches', icon: 'mdi-barrel', to: '/batches' },
        { title: 'Scenarios', icon: 'mdi-test-tube', to: '/scenarios' }
      )
    }

    // Analytics & Optimization
    if (isOwner || permissionsStore?.hasPermission('analytics.view')) {
      items.push(
        { title: 'Cost Analysis', icon: 'mdi-calculator', to: '/costs' },
        { title: 'Advanced Optimization', icon: 'mdi-rocket-launch', to: '/advanced-optimization' },
        { title: 'Profit Projections', icon: 'mdi-chart-bell-curve-cumulative', to: '/profit-projections' },
        { title: 'Volume Forecasting', icon: 'mdi-chart-timeline-variant', to: '/volume-forecasting' }
      )
    }

    // Sales & Clients
    if (isOwner || permissionsStore?.hasPermission('organizations.view') || permissionsStore?.hasPermission('clients.view') || permissionsStore?.hasPermission('orders.view')) {
      items.push(
        { title: 'Clients', icon: 'mdi-account-group', to: '/clients' },
        { title: 'Orders & deliveries', icon: 'mdi-truck-delivery-outline', to: '/sales-orders' }
      )
    }

    // Content Management
    if (isOwner || permissionsStore?.hasPermission('content.view') || permissionsStore?.hasPermission('content.manage')) {
      items.push(
        { title: 'Content Library', icon: 'mdi-image-multiple', to: '/content' },
        { title: 'Content Hub', icon: 'mdi-image-filter-hdr', to: '/content-hub' },
        { title: 'Content Studio', icon: 'mdi-movie-edit', to: '/content-studio' }
      )
    }

    // Vendors & Pricing
    if (isOwner || permissionsStore?.hasPermission('vendors.view') || permissionsStore?.hasPermission('materials.view')) {
      items.push(
        { title: 'Vendors', icon: 'mdi-truck', to: '/vendors' },
        { title: 'Vendor Pricing', icon: 'mdi-currency-usd', to: '/vendor-pricing' }
      )
    }

    // QR Redirects
    if (isOwner || permissionsStore?.hasPermission('qr_redirects.view')) {
      items.push({ title: 'QR Redirects', icon: 'mdi-qrcode', to: '/qr-redirects' })
    }

    // Newsletter subscribers — the other end of a marked QR code
    if (isOwner || permissionsStore?.hasPermission('newsletter.view')) {
      items.push({ title: 'Subscribers', icon: 'mdi-email-multiple-outline', to: '/newsletter-subscribers' })
    }

    // Company Documents
    if (isOwner || hasAdminPermissions) {
      items.push({ title: 'Company Documents', icon: 'mdi-file-certificate', to: '/company-documents' })
    }

    // Events revenue engine
    if (isOwner || permissionsStore?.hasPermission('events.view')) {
      items.push(
        { title: 'Today', icon: 'mdi-bell-ring-outline', to: '/events/today' },
        { title: 'Event queue', icon: 'mdi-calendar-star', to: '/events' },
        { title: 'Event sources', icon: 'mdi-rss', to: '/events/sources' }
      )
    }

    // Retail CRM. "Field today" rather than "Today" so it does not collide with
    // the events entry of the same name two groups above.
    if (isOwner || permissionsStore?.hasPermission('crm.view')) {
      items.push(
        { title: 'Field today', icon: 'mdi-map-marker-path', to: '/crm/today' },
        { title: 'Accounts', icon: 'mdi-storefront-outline', to: '/crm/accounts' },
        { title: 'Pipeline', icon: 'mdi-view-column-outline', to: '/crm/pipeline' },
        { title: 'Route calendar', icon: 'mdi-calendar-week', to: '/crm/calendar' },
        { title: 'Imports', icon: 'mdi-file-upload-outline', to: '/crm/imports' },
        { title: 'Sales dashboard', icon: 'mdi-chart-box-outline', to: '/crm/dashboard' }
      )
    }

    // Fan art — what the public made, waiting on a human before it joins the mosaic.
    if (isOwner || permissionsStore?.hasPermission('fanart.view')) {
      items.push({ title: 'Fan art', icon: 'mdi-palette', to: '/fan-art' })
    }

    // Trade universe. Default interfaceMode is Advanced, so these have to live
    // in this tree too — otherwise the confirmation queue has no nav entry.
    if (isOwner || permissionsStore?.hasPermission('trade.view')) {
      items.push(
        { title: 'Trade map', icon: 'mdi-map-marker-radius', to: '/trade/map' },
        { title: 'Placements', icon: 'mdi-package-variant-closed-check', to: '/trade/placements' }
      )
    }

    // Compliance
    if (isOwner || permissionsStore?.hasPermission('compliance.view')) {
      items.push(
        { title: 'Compliance', icon: 'mdi-shield-check', to: '/compliance' },
        { title: 'Obligations', icon: 'mdi-calendar-clock', to: '/compliance/obligations' },
        { title: 'Filings', icon: 'mdi-file-document-edit', to: '/compliance/form-submissions' },
        { title: 'Licenses', icon: 'mdi-certificate', to: '/compliance/licenses' },
        { title: 'Requirements', icon: 'mdi-clipboard-check', to: '/compliance/requirements' },
        { title: 'Compliance Records', icon: 'mdi-folder-check', to: '/compliance/records' }
      )
    }

    // API Keys (owner / super-admin)
    if (isOwner || permissionsStore?.hasPermission('api_keys.manage')) {
      items.push({ title: 'API Keys', icon: 'mdi-key-variant', to: '/api-keys' })
    }

    // Admin
    if (isOwner || permissionsStore?.hasPermission('users.manage_permissions')) {
      items.push(
        { title: 'User Management', icon: 'mdi-account-cog', to: '/admin/users' },
        { title: 'Company Settings', icon: 'mdi-cog', to: '/settings' }
      )
    }
  }

  return items
})

const currentPageTitle = computed(() => {
  const item = menuItems.value.find(item => item.to === route.path)
  return item ? item.title : 'Beverage Forecast'
})

const isLoginPage = computed(() => route.path === '/login')

const refreshData = () => {
  window.location.reload()
}

// User authentication
const currentUser = computed(() => {
  if (authStore?.user) {
    return `${authStore.user.firstName} ${authStore.user.lastName}`
  }
  return ''
})

const isLoggedIn = computed(() => authStore?.isAuthenticated || false)
const userRole = computed(() => authStore?.user?.role || '')

onMounted(async () => {
  // Initialize auth first
  try {
    await authStore.initializeAuth();
  } catch (error) {
    console.error('App.vue: Auth initialization failed:', error);
  }

  // Fetch current user and permissions if logged in
  if (authStore.isAuthenticated) {
    try {
      await authStore.fetchCurrentUser();
      await permissionsStore.refreshPermissions();
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  }

  // Initialize permissions and units if user is logged in
  if (authStore.isAuthenticated) {
    try {
      await Promise.all([
        permissionsStore.initMyPermissions(),
        unitsStore.fetchUnits()
      ]);
    } catch (error) {
      console.error('App.vue: Failed to load permissions/units:', error)
    }
  }
})

const handleProfile = () => {
  router.push('/profile')
}

const handleSettings = () => {
  router.push('/settings')
}

const handleLogout = async () => {
  try {
    // Call logout endpoint to invalidate session on server
    await axios.post('/api/v1/admin/auth/logout')
  } catch (error) {
    // Even if logout fails, continue with local cleanup
    console.error('Logout request failed:', error)
  }

  // Clear stores
  authStore?.logout()
  permissionsStore?.$reset()

  // Clear local storage
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('client')
  delete axios.defaults.headers.common['Authorization']

  router.push('/login')
}

const handleLogin = () => {
  router.push('/login')
}
</script>

<style scoped>
/* Global styles now imported via SCSS in main.js */

/* Admin uses the shared font system from SCSS */
/* Primary font is applied via shared styles */

/* Override app bar button colors to use brand primary instead of burgundy */
.v-app-bar .v-btn {
  color: rgb(var(--v-theme-primary)) !important;
}

/* Custom class for app bar buttons to use unified black color */
.v-app-bar .app-bar-btn-primary,
.v-app-bar .app-bar-btn-primary .v-btn__content,
.v-app-bar .app-bar-btn-primary .v-icon,
.app-bar-btn-primary,
.app-bar-btn-primary .v-btn__content,
.app-bar-btn-primary .v-icon {
  color: rgba(0, 0, 0, 0.87) !important;
}

/* Form inputs and labels should use UI font family - override inherit rule */
.v-application .v-field__input,
.v-application .v-field-label,
.v-application .v-label,
.v-application input[type="text"],
.v-application input[type="email"],
.v-application input[type="password"],
.v-application input[type="number"],
.v-application textarea {
  font-family: var(--font-ui, 'Helvetica Neue', Arial, sans-serif) !important;
}

/* Form labels should use neutral color instead of orange */
.v-application .v-field-label,
.v-application .v-label {
  color: rgba(0, 0, 0, 0.6) !important;
}

html {
  overflow-y: auto !important;
}

/* Navigation drawer offset from app bar */
.navigation-drawer-offset {
  top: 60px !important;
  height: calc(100% - 60px) !important;
}

/* Make drawer expandable/collapsible */
.v-navigation-drawer--rail {
  width: 56px !important;
}

.v-navigation-drawer--rail .v-list-item__prepend {
  margin-inline-end: 0 !important;
}
/* Hide title text in rail mode */
.v-navigation-drawer--rail .v-list-item-title {
  display: none !important;
}

/* Tooltip animations and styling */
.v-tooltip > .v-overlay__content {
  background: rgba(33, 33, 33, 0.95) !important;
  color: white !important;
  padding: 8px 16px !important;
  border-radius: 8px !important;
  font-size: 14px !important;
  font-weight: 500 !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25) !important;
  backdrop-filter: blur(10px) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  animation: tooltipFadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

/* Dynamic tooltip arrow styling based on location */
/* For tooltips positioned on TOP (arrow points down) */
.v-overlay--location-top .v-overlay__content::before {
  content: "";
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: rgba(33, 33, 33, 0.95);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* For tooltips positioned on BOTTOM (arrow points up) */
.v-overlay--location-bottom .v-overlay__content::before {
  content: "";
  position: absolute;
  top: -6px;
  left: 50%;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: rgba(33, 33, 33, 0.95);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

/* For tooltips positioned on LEFT/START (arrow points right) */
.v-overlay--location-left .v-overlay__content::before,
.v-overlay--location-start .v-overlay__content::before {
  content: "";
  position: absolute;
  top: 50%;
  right: -6px;
  transform: translateY(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: rgba(33, 33, 33, 0.95);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
}

/* For tooltips positioned on RIGHT/END (arrow points left) */
.v-overlay--location-right .v-overlay__content::before,
.v-overlay--location-end .v-overlay__content::before {
  content: "";
  position: absolute;
  top: 50%;
  left: -6px;
  transform: translateY(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: rgba(33, 33, 33, 0.95);
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

/* Navigation item hover effects */
.nav-item-with-tooltip {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative;
  overflow: hidden;
}

.nav-item-with-tooltip:hover {
  transform: translateX(2px);
}

.nav-item-with-tooltip:hover .v-list-item__prepend .v-icon {
  transform: scale(1.1);
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Ripple effect on hover */
.nav-item-with-tooltip::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(var(--v-theme-primary), 0.1);
  transform: translate(-50%, -50%);
  transition: width 0.3s, height 0.3s;
}

.nav-item-with-tooltip:hover::after {
  width: 100%;
  height: 100%;
}

/* Tooltip fade-in animation */
@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}


/* Enhanced shadow on hover */
.nav-item-with-tooltip:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Sub-menu item styling */
.nav-sub-item {
  padding-left: 32px !important;
  font-size: 0.875rem !important;
}

.nav-sub-item .v-list-item__prepend {
  margin-inline-end: 12px !important;
}

.nav-sub-item .v-list-item__prepend .v-icon {
  font-size: 18px !important;
  opacity: 0.7;
}
</style>

<style>
/* Remove opacity from list item subtitles to make production data clearly visible */
.v-list-item-subtitle {
  opacity: 1 !important;
}
</style>
